import React, { forwardRef, useId } from "react";

import { cn } from "src/shadcn/lib/utils";
import { Checkbox as PrimitiveCheckbox } from "src/primitives/Checkbox";
import {
  Field,
  FieldContent,
  FieldLabel,
  FieldDescription,
  FieldError,
} from "src/primitives/Field";

export interface CheckboxProps extends React.ComponentProps<
  typeof PrimitiveCheckbox
> {
  /** Text displayed next to the checkbox. */
  label?: string;
  /** Error message displayed below the checkbox. */
  error?: string;
  /** Helper text displayed below the label. Accepts string or ReactNode. */
  helpText?: React.ReactNode;
  /** Shows a required indicator next to the label. */
  required?: boolean;
  /** Additional class name for the outermost wrapper. */
  className?: string;
  /** Props forwarded to the Label element. */
  labelProps?: React.ComponentProps<typeof FieldLabel>;
}

const Checkbox = forwardRef<
  React.ComponentRef<typeof PrimitiveCheckbox>,
  CheckboxProps
>(
  (
    {
      label,
      error,
      helpText,
      required = false,
      disabled = false,
      className,
      labelProps,
      ...otherProps
    },
    ref
  ) => {
    const generatedId = useId();
    const id = otherProps.id ?? generatedId;
    const errorId = `error_${id}`;
    const helpTextId = `helpText_${id}`;

    const ariaDescribedBy =
      [error ? errorId : null, helpText ? helpTextId : null]
        .filter(Boolean)
        .join(" ") || undefined;

    const hasField = !!(label || error || helpText);

    if (!hasField) {
      return (
        <PrimitiveCheckbox
          ref={ref}
          id={id}
          disabled={disabled}
          aria-invalid={!!error || undefined}
          aria-required={required || undefined}
          aria-describedby={ariaDescribedBy}
          className={className}
          {...otherProps}
        />
      );
    }

    return (
      <div
        className={cn("flex flex-col gap-1", className)}
        data-disabled={disabled || undefined}
        data-invalid={!!error || undefined}
      >
        <Field orientation="horizontal">
          <PrimitiveCheckbox
            ref={ref}
            id={id}
            disabled={disabled}
            aria-invalid={!!error || undefined}
            aria-required={required || undefined}
            aria-describedby={ariaDescribedBy}
            {...otherProps}
          />
          <FieldContent>
            {label && (
              <FieldLabel htmlFor={id} {...labelProps}>
                {label}
                {required && (
                  <span aria-hidden="true" className="text-destructive">
                    *
                  </span>
                )}
              </FieldLabel>
            )}
            {helpText && (
              <FieldDescription id={helpTextId}>{helpText}</FieldDescription>
            )}
          </FieldContent>
        </Field>
        {error && <FieldError id={errorId}>{error}</FieldError>}
      </div>
    );
  }
);

Checkbox.displayName = "Checkbox";

export { Checkbox };
